import { useCallback, useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import { BOOST_AMOUNT, boostDailyViews, sumViewsByDateKey, toDateKey } from '@/lib/trackPageView'
import { getDisplayedLikes, getDisplayedShares, formatCount } from '@/lib/engagement'
import {Eye, Heart, Loader2, Rocket, Share2, Zap} from 'lucide-react'
import toast from 'react-hot-toast'
import type { NewsArticle, PageView } from '@/types'

function todayKey() {
  return toDateKey()
}

const PRESETS = [10, 50, 100, 250, 500]

function parseAmount(value: string): number {
  const n = Number.parseInt(value.replace(/\D/g, ''), 10)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.min(n, 100000)
}

export default function Booster() {
  const [todayViews, setTodayViews] = useState(0)
  const [loading, setLoading] = useState(true)
  const [boosting, setBoosting] = useState(false)
  const [lastAdded, setLastAdded] = useState<number | null>(null)
  const [articles, setArticles] = useState<NewsArticle[]>([])
  const [articlesLoading, setArticlesLoading] = useState(true)
  const [selectedId, setSelectedId] = useState('')
  const [likeAmount, setLikeAmount] = useState('50')
  const [shareAmount, setShareAmount] = useState('50')
  const [viewAmount, setViewAmount] = useState('50')
  const [applyingLikes, setApplyingLikes] = useState(false)
  const [applyingShares, setApplyingShares] = useState(false)
  const [applyingViews, setApplyingViews] = useState(false)

  const fetchToday = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.page_views.list({
        filter: { dateKey: todayKey() },
        limit: 20,
      })
      const byDay = sumViewsByDateKey((list || []) as PageView[])
      setTodayViews(byDay.get(todayKey()) || 0)
    } catch {
      toast.error('Erro ao carregar as visualizações de hoje.')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchArticles = useCallback(async () => {
    setArticlesLoading(true)
    try {
      const { list } = await lumi.entities.news_articles.list({
        sort: { createdAt: -1 },
        limit: 200,
      })
      const next = (list || []) as NewsArticle[]
      setArticles(next)
      setSelectedId((prev) => prev || next[0]?._id || '')
    } catch {
      toast.error('Erro ao carregar as notícias.')
    } finally {
      setArticlesLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchToday()
    fetchArticles()
  }, [fetchToday, fetchArticles])

  const selected = articles.find((a) => a._id === selectedId)
  const startIso = selected?.publishedAt || selected?.createdAt
  const displayedLikes = selected ? getDisplayedLikes(selected.likes, startIso) : 0
  const displayedShares = selected ? getDisplayedShares(selected.shares, startIso) : 0
  const displayedViews = selected ? Math.max(0, selected.views || 0) : 0
  const likeAdd = parseAmount(likeAmount)
  const shareAdd = parseAmount(shareAmount)
  const viewAdd = parseAmount(viewAmount)
  const busy = applyingLikes || applyingShares || applyingViews

  const handleBoost = async () => {
    if (boosting) return
    setBoosting(true)
    try {
      const result = await boostDailyViews(BOOST_AMOUNT)
      setTodayViews(result.views)
      setLastAdded(result.added)
      toast.success(`+${result.added.toLocaleString('pt-PT')} visualizações somadas ao dia de hoje.`)
    } catch {
      toast.error('Não foi possível aplicar o booster.')
    } finally {
      setBoosting(false)
    }
  }

  const applyEngagement = async (kind: 'likes' | 'shares' | 'views') => {
    const articleId = selected?._id
    if (!articleId || typeof articleId !== 'string') {
      toast.error('Seleccione uma matéria.')
      return
    }
    const amount = kind === 'likes' ? likeAdd : kind === 'shares' ? shareAdd : viewAdd
    if (amount <= 0) {
      toast.error('Indique uma quantidade válida (1 a 100 000).')
      return
    }
    if (kind === 'likes') setApplyingLikes(true)
    else if (kind === 'shares') setApplyingShares(true)
    else setApplyingViews(true)
    try {
      const current =
        kind === 'likes' ? selected.likes || 0 : kind === 'shares' ? selected.shares || 0 : selected.views || 0
      const nextValue = current + amount
      const now = new Date().toISOString()
      await lumi.entities.news_articles.update(articleId, {
        [kind]: nextValue,
        updatedAt: now,
      })
      setArticles((prev) =>
        prev.map((item) =>
          item._id === articleId ? { ...item, [kind]: nextValue, updatedAt: now } : item,
        ),
      )
      const label =
        kind === 'likes' ? 'curtidas' : kind === 'shares' ? 'partilhas' : 'visualizações'
      toast.success(`+${amount.toLocaleString('pt-PT')} ${label} somadas a “${selected.title || 'Matéria'}”.`)
    } catch {
      toast.error(
        kind === 'likes'
          ? 'Não foi possível aplicar o booster de curtidas.'
          : kind === 'shares'
            ? 'Não foi possível aplicar o booster de partilhas.'
            : 'Não foi possível aplicar o booster de visualizações.',
      )
    } finally {
      if (kind === 'likes') setApplyingLikes(false)
      else if (kind === 'shares') setApplyingShares(false)
      else setApplyingViews(false)
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <div className="mb-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-red-700">
          CARLOS FELIPE
        </p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">Booster</h1>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          Ferramenta exclusiva de administrador. Impulsione visualizações do dia, ou escolha uma matéria e some curtidas, partilhas e visualizações.
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
        <div className="border-b border-zinc-100 px-5 py-4 dark:border-zinc-800">
          <div className="flex items-center gap-2">
            <Rocket size={16} className="text-red-600" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Impulso diário</h2>
          </div>
        </div>

        <div className="grid grid-cols-2 divide-x divide-zinc-100 dark:divide-zinc-800">
          <div className="px-5 py-5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Hoje</p>
            <p className="mt-1 text-3xl font-bold tabular-nums text-gray-900 dark:text-white">
              {loading ? '—' : todayViews.toLocaleString('pt-PT')}
            </p>
            <p className="mt-1 text-xs text-gray-400">Visitas reais + booster</p>
          </div>
          <div className="px-5 py-5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Por clique</p>
            <p className="mt-1 text-3xl font-bold tabular-nums text-red-600">
              +{BOOST_AMOUNT.toLocaleString('pt-PT')}
            </p>
            <p className="mt-1 text-xs text-gray-400">
              {lastAdded
                ? `Último impulso: +${lastAdded.toLocaleString('pt-PT')}`
                : 'Pode clicar várias vezes'}
            </p>
          </div>
        </div>

        <div className="border-t border-zinc-100 px-5 py-5 dark:border-zinc-800">
          <button
            type="button"
            onClick={handleBoost}
            disabled={boosting || loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 sm:w-auto"
          >
            {boosting ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
            {boosting ? 'A aplicar...' : `Aplicar +${BOOST_AMOUNT.toLocaleString('pt-PT')} visualizações`}
          </button>
          <p className="mt-3 text-xs leading-relaxed text-gray-400">
            O valor entra no mesmo contador do painel (Visão Geral). As visitas reais continuam a ser somadas normalmente.
          </p>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950">
        <div className="border-b border-zinc-100 px-5 py-4 dark:border-zinc-800">
          <div className="flex items-center gap-2">
            <Heart size={16} className="text-red-600" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              Curtidas, partilhas e visualizações por matéria
            </h2>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-gray-400">
            Escolha a notícia e a quantidade. Os valores são somados aos contadores reais e aparecem no site e no Top 10 da semana.
          </p>
        </div>

        <div className="space-y-5 px-5 py-5">
          <div>
            <label htmlFor="booster-article" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Matéria
            </label>
            <select
              id="booster-article"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              disabled={articlesLoading || articles.length === 0}
              className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
            >
              {articlesLoading ? (
                <option value="">A carregar notícias...</option>
              ) : articles.length === 0 ? (
                <option value="">Nenhuma matéria encontrada</option>
              ) : (
                articles.map((article) => (
                  <option key={article._id} value={article._id}>
                    {article.title || 'Sem título'}
                  </option>
                ))
              )}
            </select>
          </div>

          {selected ? (
            <div className="grid grid-cols-3 gap-3 rounded-xl bg-zinc-50 px-4 py-3 dark:bg-zinc-900/60">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Curtidas visíveis</p>
                <p className="mt-0.5 text-lg font-bold tabular-nums text-gray-900 dark:text-white">
                  {formatCount(displayedLikes)}
                </p>
                <p className="text-[11px] text-gray-400">
                  Base: {(selected.likes || 0).toLocaleString('pt-PT')}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Partilhas visíveis</p>
                <p className="mt-0.5 text-lg font-bold tabular-nums text-gray-900 dark:text-white">
                  {formatCount(displayedShares)}
                </p>
                <p className="text-[11px] text-gray-400">
                  Base: {(selected.shares || 0).toLocaleString('pt-PT')}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Visualizações</p>
                <p className="mt-0.5 text-lg font-bold tabular-nums text-gray-900 dark:text-white">
                  {formatCount(displayedViews)}
                </p>
                <p className="text-[11px] text-gray-400">
                  Base: {(selected.views || 0).toLocaleString('pt-PT')}
                </p>
              </div>
            </div>
          ) : null}

          <div className="grid gap-5 sm:grid-cols-3">
            <div className="rounded-xl border border-zinc-100 p-4 dark:border-zinc-800">
              <div className="mb-3 flex items-center gap-2">
                <Heart size={14} className="text-red-600" />
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Curtidas</p>
              </div>
              <label htmlFor="booster-likes" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Quantidade a somar
              </label>
              <input
                id="booster-likes"
                type="number"
                min={1}
                max={100000}
                inputMode="numeric"
                value={likeAmount}
                onChange={(e) => setLikeAmount(e.target.value)}
                className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm tabular-nums text-gray-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {PRESETS.map((n) => (
                  <button
                    key={`like-${n}`}
                    type="button"
                    onClick={() => setLikeAmount(String(n))}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      likeAdd === n
                        ? 'bg-red-600 text-white'
                        : 'bg-zinc-100 text-gray-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-gray-300 dark:hover:bg-zinc-700'
                    }`}
                  >
                    +{n}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => applyEngagement('likes')}
                disabled={!selected || busy || likeAdd <= 0}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {applyingLikes ? <Loader2 size={15} className="animate-spin" /> : <Heart size={15} />}
                {applyingLikes ? 'A aplicar...' : `Somar +${likeAdd.toLocaleString('pt-PT')} curtidas`}
              </button>
            </div>

            <div className="rounded-xl border border-zinc-100 p-4 dark:border-zinc-800">
              <div className="mb-3 flex items-center gap-2">
                <Share2 size={14} className="text-red-600" />
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Partilhas</p>
              </div>
              <label htmlFor="booster-shares" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Quantidade a somar
              </label>
              <input
                id="booster-shares"
                type="number"
                min={1}
                max={100000}
                inputMode="numeric"
                value={shareAmount}
                onChange={(e) => setShareAmount(e.target.value)}
                className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm tabular-nums text-gray-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {PRESETS.map((n) => (
                  <button
                    key={`share-${n}`}
                    type="button"
                    onClick={() => setShareAmount(String(n))}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      shareAdd === n
                        ? 'bg-red-600 text-white'
                        : 'bg-zinc-100 text-gray-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-gray-300 dark:hover:bg-zinc-700'
                    }`}
                  >
                    +{n}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => applyEngagement('shares')}
                disabled={!selected || busy || shareAdd <= 0}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {applyingShares ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />}
                {applyingShares ? 'A aplicar...' : `Somar +${shareAdd.toLocaleString('pt-PT')} partilhas`}
              </button>
            </div>

            <div className="rounded-xl border border-zinc-100 p-4 dark:border-zinc-800">
              <div className="mb-3 flex items-center gap-2">
                <Eye size={14} className="text-red-600" />
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Visualizações</p>
              </div>
              <label htmlFor="booster-views" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Quantidade a somar
              </label>
              <input
                id="booster-views"
                type="number"
                min={1}
                max={100000}
                inputMode="numeric"
                value={viewAmount}
                onChange={(e) => setViewAmount(e.target.value)}
                className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm tabular-nums text-gray-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {PRESETS.map((n) => (
                  <button
                    key={`view-${n}`}
                    type="button"
                    onClick={() => setViewAmount(String(n))}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                      viewAdd === n
                        ? 'bg-red-600 text-white'
                        : 'bg-zinc-100 text-gray-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-gray-300 dark:hover:bg-zinc-700'
                    }`}
                  >
                    +{n}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => applyEngagement('views')}
                disabled={!selected || busy || viewAdd <= 0}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {applyingViews ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />}
                {applyingViews ? 'A aplicar...' : `Somar +${viewAdd.toLocaleString('pt-PT')} visualizações`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
